// @flow

import merge from 'lodash/merge';
import type {
  Theme, ButtonScheme, GradientScheme, ColorScheme,
} from '../types/theme';
import * as FontWeights from '../helpers/fontWeight';
import { breakpoints } from '../helpers';

type DefaultThemeParams = {
  BRAND_PRIMARY: string,
  BRAND_SECONDARY: string,
  BRAND_SECONDARY_DARK: string,
  BRAND_TERTIARY: string,
  BRAND_PRIMARY_FROM: string,
  BRAND_PRIMARY_TO: string,
  WHITE: string,
  SILVER: string,
  GRAY: string,
  GRAY_DARK: string,
  BLACK: string,
  ERROR: string,
  SUCCESS: string,
  FONT_DEFAULT: string,
  FONT_HEADING: string,
  FONT_ACCENT: string,
  FONT_WEIGHT_DEFAULT: number,
  FONT_WEIGHT_HEADING: number,
  FONT_WEIGHT_ACCENT: number,
  TEXT_DEFAULT: string,
  TEXT_SECONDARY: string,
  TEXT_TERTIARY: string,
  TEXT_HERO: string,
  TEXT_LIGHT: string,
  HEADING_DEFAULT: string,
  BACKGROUND_DEFAULT: string,
  BACKGROUND_LIGHT: string,
  BACKGROUND_DARK: string,
  BACKGROUND_ERROR: string,
  BACKGROUND_SUCCESS: string,
  FONT_SIZE_H1: string,
  FONT_SIZE_H2: string,
  FONT_SIZE_H3: string,
  FONT_SIZE_H4: string,
  FONT_SIZE_H5: string,
  FONT_SIZE_H6: string,
  FONT_SIZE_DEFAULT: string,
  FONT_SIZE_SMALL: string,
  FONT_SIZE_TINY: string,
  BOX_SHADOW: string,
  DROP_SHADOW: string,
  BORDER_RADIUS_DEFAULT: string,
  BORDER_RADIUS_SMALL: string,
  BORDER_COLOR: string,
  PRIMARY_BTN_SCHEME: ButtonScheme,
  PRIMARY_GRADIENT_SCHEME?: GradientScheme,
  SECONDARY_BTN_SCHEME: ButtonScheme,
  TERTIARY_BTN_SCHEME: ButtonScheme,
  LIGHT_BTN_SCHEME: ButtonScheme,
  MAIN_MENU_SCHEME: ButtonScheme,
  DISABLED_BTN_SCHEME: ButtonScheme,
  DEFAULT_SCHEME: ColorScheme,
  DEFAULT_LIGHT_SCHEME: ColorScheme,
  WHITE_SCHEME: ColorScheme,
  LIGHT_SCHEME: ColorScheme,
  DARK_SCHEME: ColorScheme,
  PRIMARY_SCHEME: ColorScheme,
  SECONDARY_SCHEME: ColorScheme,
  TERTIARY_SCHEME: ColorScheme,
};

const defaultTheme = ({
  BRAND_PRIMARY,
  BRAND_PRIMARY_FROM,
  BRAND_PRIMARY_TO,
  BRAND_SECONDARY,
  BRAND_SECONDARY_DARK,
  BRAND_TERTIARY,
  WHITE,
  SILVER,
  GRAY,
  GRAY_DARK,
  BLACK,
  ERROR,
  SUCCESS,
  FONT_ACCENT,
  TEXT_DEFAULT,
  TEXT_SECONDARY,
  TEXT_TERTIARY,
  TEXT_LIGHT,
  HEADING_DEFAULT,
  BACKGROUND_DEFAULT,
  BACKGROUND_LIGHT,
  BACKGROUND_DARK,
  BACKGROUND_ERROR,
  BACKGROUND_SUCCESS,
  FONT_SIZE_H1,
  FONT_SIZE_H2,
  FONT_SIZE_H3,
  FONT_SIZE_H4,
  FONT_SIZE_H5,
  FONT_SIZE_H6,
  FONT_SIZE_DEFAULT,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  BOX_SHADOW,
  DROP_SHADOW,
  BORDER_RADIUS_DEFAULT,
  BORDER_RADIUS_SMALL,
  BORDER_COLOR,
  PRIMARY_BTN_SCHEME,
  SECONDARY_BTN_SCHEME,
  TERTIARY_BTN_SCHEME,
  LIGHT_BTN_SCHEME,
  MAIN_MENU_SCHEME,
  DISABLED_BTN_SCHEME,
  DEFAULT_SCHEME,
  DEFAULT_LIGHT_SCHEME,
  WHITE_SCHEME,
  LIGHT_SCHEME,
  DARK_SCHEME,
  PRIMARY_SCHEME,
  SECONDARY_SCHEME,
  TERTIARY_SCHEME,
}: DefaultThemeParams) => ({
  loader: {
    color: BRAND_PRIMARY,
    height: '0.25rem',
  },
  techSpecs: {
    price: {
      color: BRAND_PRIMARY,
    },
  },
  package: {
    titleColor: BLACK,
    priceColor: BLACK,
    soldOut: {
      backgroundColor: BRAND_SECONDARY,
      background: 'linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)) 0 0 no-repeat rgba(0, 0, 0, 0.15)',
      color: TEXT_LIGHT,
      fontSize: FONT_SIZE_DEFAULT,
    },
    highlights: {
      titleFontSize: FONT_SIZE_DEFAULT,
      iconColor: TEXT_TERTIARY,
    },
  },
  ribbon: {
    color: WHITE,
    backgroundColor: BRAND_PRIMARY_TO,
    backgroundGradient: `linear-gradient(to bottom, ${BRAND_PRIMARY_FROM}, ${BRAND_PRIMARY_TO});`,
    filter: 'drop-shadow(0 0.1rem 0.1rem rgba(0, 0, 0, .6))',
  },
  compareTable: {
    addButton: {
      iconColor: WHITE,
      color: BRAND_SECONDARY,
      iconBackground: BRAND_SECONDARY,
    },
  },
  table: {
    borderColor: BORDER_COLOR,
    titleRow: {
      color: BRAND_PRIMARY,
      backgroundColor: BRAND_PRIMARY,
    },
    titleCell: {
      color: BRAND_PRIMARY,
      backgroundColor: GRAY,
    },
  },
  pdbCompare: {
    height: '12.65rem',
    width: '12.65rem',
    shadow: BOX_SHADOW,
    borderColor: BORDER_COLOR,
    titleColor: BRAND_PRIMARY,
    subtitleColor: BRAND_PRIMARY,
    removeIcon: BRAND_PRIMARY,
    color: {
      titleColor: BRAND_PRIMARY,
      priceColor: TEXT_SECONDARY,
    },
  },
  title: {
    color: BRAND_PRIMARY,
  },
  assortment: {
    resultCount: {
      subtitleColor: GRAY,
    },
    filterBar: {
      backgroundColor: SILVER,
    },
    activeFiltersButton: {
      backgroundColor: BRAND_SECONDARY,
      color: WHITE,
    },
    activeFilters: {
      backgroundColor: BRAND_PRIMARY,
      color: BRAND_SECONDARY,
    },
    facets: {
      title: {
        color: HEADING_DEFAULT,
        fontSize: FONT_SIZE_H5,
      },
      count: {
        color: GRAY,
      },
      backgroundColor: {
        default: WHITE,
        drawer: BACKGROUND_LIGHT,
      },
    },
  },

  breakpoints: Object.values(breakpoints),
  background: {
    default: BACKGROUND_DEFAULT,
    light: BACKGROUND_LIGHT,
    dark: BACKGROUND_DARK,
    primary: BRAND_PRIMARY,
    secondary: BRAND_SECONDARY,
    tertiary: BRAND_TERTIARY,
  },

  buttons: {
    borderRadius: BORDER_RADIUS_SMALL,
    fontWeight: FontWeights.REGULAR,
    fontSize: FONT_SIZE_DEFAULT,
    textTransform: 'none',
    minWidth: '10.25em',
    padding: {
      default: '0.875em 2em',
      small: '0.563em 2em',
      tiny: '0 1em',
    },
    paddingWithIcon: {
      default: '0.875em 1em 0.875em 0.5em',
      small: '0.563em 1em 0.563em 0.5em',
      tiny: '0 1em 0 0.5em',
    },
    scheme: {
      default: SECONDARY_BTN_SCHEME,
      primary: PRIMARY_BTN_SCHEME,
      secondary: SECONDARY_BTN_SCHEME,
      tertiary: TERTIARY_BTN_SCHEME,
      light: LIGHT_BTN_SCHEME,
      disabled: DISABLED_BTN_SCHEME,
    },
  },
  carblock: {
    buttonProps: {
      fullWidth: true,
      colors: 'secondary',
      size: 'small',
    },
    background: BACKGROUND_DEFAULT,
    color: TEXT_SECONDARY,
    borderRadius: BORDER_RADIUS_DEFAULT,
    boxShadow: BOX_SHADOW,
    discount: {
      backgroundColor: BRAND_TERTIARY,
      color: TEXT_LIGHT,
      fontSize: FONT_SIZE_TINY,
    },
    payment: {
      firstLineFontSize: FONT_SIZE_SMALL,
      lineHeight: '1rem',
      width: '3.75rem',
      fontSize: `calc(${FONT_SIZE_SMALL} * .8)`,
    },
    wrapper: {
      margin: {
        sm: '0 0 .4rem',
        md: '0 0 .4rem',
      },
      padding: {
        sm: '1.25rem 1.5625rem',
        md: '1.25rem 1.5625rem',
        lg: '1.25rem 1.5625rem',
      },
    },
    packageName: {
      color: BRAND_PRIMARY,
      fontWeight: FontWeights.REGULAR,
      fontSize: {
        default: FONT_SIZE_H6,
        lg: FONT_SIZE_H5,
      },
    },
    specs: {
      fontWeight: FontWeights.REGULAR,
      color: TEXT_SECONDARY,
      fontSize: {
        default: FONT_SIZE_SMALL,
        lg: FONT_SIZE_SMALL,
      },
    },
    title: {
      color: HEADING_DEFAULT,
      fontSize: '1.25rem',
    },
    price: {
      color: BRAND_PRIMARY,
      fontSize: {
        default: FONT_SIZE_H2,
        lg: FONT_SIZE_H1,
      },
    },
    fromPrice: {
      color: BRAND_SECONDARY,
      fontSize: FONT_SIZE_SMALL,
    },
    priceJustifyContent: 'flex-start',
    remarks: {
      color: TEXT_SECONDARY,
      fontSize: '.7rem',
    },
    capsule: {
      backgroundColor: BRAND_SECONDARY,
      color: TEXT_LIGHT,
      fontSize: FONT_SIZE_DEFAULT,
    },
  },

  card: {
    backgroundColor: BACKGROUND_DEFAULT,
    borderColor: BORDER_COLOR,
    borderRadius: BORDER_RADIUS_DEFAULT,
    boxShadow: BOX_SHADOW,
    padding: '1.5rem',
  },

  cart: {
    backgroundColor: BACKGROUND_DEFAULT,
    borderColor: BORDER_COLOR,
    borderRadius: BORDER_RADIUS_DEFAULT,
    titleColor: HEADING_DEFAULT,
    fontSize: FONT_SIZE_H5,
    saveButton: {
      borderColor: BORDER_COLOR,
      borderRadius: BORDER_RADIUS_DEFAULT,
      color: BORDER_COLOR,
    },
    subTitle: {
      color: TEXT_SECONDARY,
      fontSize: FONT_SIZE_SMALL,
    },
    textColor: TEXT_DEFAULT,
  },

  cartColumn: {
    heading: {
      background: BRAND_TERTIARY,
      color: TEXT_LIGHT,
      fontSize: FONT_SIZE_H5,
    },
  },

  column: {
    padding: '2rem',
    hero: {
      padding: '5.5rem',
    },
  },

  configChoice: {
    titleColor: HEADING_DEFAULT,
    descriptionColor: TEXT_SECONDARY,
    borderColor: BORDER_COLOR,
    errorBackground: ERROR,
    errorColor: WHITE,
    errorBorderRadius: BORDER_RADIUS_SMALL,
  },

  configColor: {
    tile: {
      fontSize: FONT_SIZE_H1,
      borderColor: BRAND_TERTIARY,
      borderRadius: BORDER_RADIUS_SMALL,
    },
    title: {
      color: TEXT_DEFAULT,
      fontSize: FONT_SIZE_H5,
    },
    stock: {
      color: SUCCESS,
      fontSize: FONT_SIZE_SMALL,
    },
    fromPrice: {
      color: BRAND_TERTIARY,
    },
    price: {
      color: TEXT_SECONDARY,
      fontSize: FONT_SIZE_SMALL,
    },
  },

  configPackage: {
    activeColor: BRAND_TERTIARY,
    soldOut: {
      backgroundColor: BRAND_SECONDARY,
      color: TEXT_LIGHT,
      fontSize: FONT_SIZE_DEFAULT,
    },
    label: {
      backgroundColor: SUCCESS,
      color: TEXT_LIGHT,
      fontSize: FONT_SIZE_TINY,
    },
    highlights: {
      fontSize: FONT_SIZE_SMALL,
      iconColor: TEXT_TERTIARY,
    },
    title: {
      fontSize: FONT_SIZE_H5,
    },
  },

  anchor: {
    main: BRAND_PRIMARY,
    hover: BRAND_PRIMARY_TO,
    active: BRAND_PRIMARY_TO,
    focus: BRAND_PRIMARY_TO,
    visited: BRAND_PRIMARY_TO,
  },

  html: {
    backgroundColor: BACKGROUND_LIGHT,
    color: BLACK,
    fontSize: FONT_SIZE_DEFAULT,
    fontWeight: FontWeights.LIGHT,
  },

  leadGenerator: {
    title: {
      color: BRAND_PRIMARY,
      h1FontSize: FONT_SIZE_H1,
      h2FontSize: FONT_SIZE_H2,
      h3FontSize: FONT_SIZE_H3,
    },
    row: {
      backgroundColor: BACKGROUND_LIGHT,
      color: BLACK,
      fontSize: FONT_SIZE_DEFAULT,
      fontWeight: FontWeights.LIGHT,
    },
  },

  companyInformation: {
    title: {
      color: BRAND_SECONDARY,
    },
  },

  textWithRightButtons: {
    title: {
      color: BRAND_PRIMARY,
      h1FontSize: FONT_SIZE_H1,
      h2FontSize: FONT_SIZE_H2,
      h3FontSize: FONT_SIZE_H3,
    },
    row: {
      color: BLACK,
      fontSize: FONT_SIZE_DEFAULT,
      fontWeight: FontWeights.LIGHT,
    },
  },

  faq: {
    categoryTitle: {
      iconColor: BRAND_PRIMARY,
      description: {
        color: TEXT_SECONDARY,
        fontWeight: FontWeights.SEMIBOLD,
      },
      title: {
        color: BRAND_PRIMARY,
        fontWeight: FontWeights.BOLD,
      },
    },
    categoryInOverview: {
      iconColor: BRAND_PRIMARY,
      title: {
        color: BRAND_PRIMARY,
        fontWeight: FontWeights.BOLD,
        fontSize: FONT_SIZE_H5,
      },
    },
    feedback: {
      borderColor: BORDER_COLOR,
      title: {
        color: BRAND_PRIMARY,
        fontWeight: FontWeights.BOLD,
      },
    },
    questionDetail: {
      question: {
        color: BRAND_PRIMARY,
        fontWeight: FontWeights.BOLD,
      },
      answer: {
        color: TEXT_DEFAULT,
        fontWeight: FontWeights.REGULAR,
        linkColor: BRAND_SECONDARY,
        linkHoverColor: BRAND_SECONDARY_DARK,
      },
    },
    questionList: {
      backgroundColor: BACKGROUND_LIGHT,
      backgroundColorHover: SILVER,
      question: {
        color: BRAND_PRIMARY,
        fontWeight: FontWeights.SEMIBOLD,
      },
    },
    subCategoryList: {
      activeItem: {
        color: BRAND_SECONDARY,
        fontWeight: FontWeights.SEMIBOLD,
      },
      backLink: {
        color: BRAND_SECONDARY,
        fontWeight: FontWeights.REGULAR,
      },
      listBorderColor: BORDER_COLOR,
    },
  },

  configBuilderGroup: {
    borderColor: BORDER_COLOR,
  },

  contactOption: {
    icon: {
      borderColor: BRAND_SECONDARY,
      iconColor: BRAND_SECONDARY,
      padding: '0.6rem',
      size: '1.2rem',
    },
    text: {
      color: BRAND_PRIMARY,
      fontSize: FONT_SIZE_DEFAULT,
    },
    subtitle: {
      color: TEXT_SECONDARY,
      fontSize: FONT_SIZE_SMALL,
    },
  },

  deliveryTime: {
    color: SUCCESS,
    fontSize: FONT_SIZE_SMALL,
  },

  divider: {
    color: BORDER_COLOR,
    marginTop: '1rem',
    marginBottom: '1rem',
  },

  form: {
    prefixedInput: {
      color: SILVER,
      border: SILVER,
    },
    rangeSlider: {
      handleBackground: WHITE,
      backgroundColor: SILVER,
      progressColor: BRAND_SECONDARY,
      handleBorder: SILVER,
    },
    checkbox: {
      borderColor: BORDER_COLOR,
      borderColorError: ERROR,
      disabledBackgroundColor: BORDER_COLOR,
      iconColor: WHITE,
    },
    error: {
      borderColor: ERROR,
      backgroundColor: ERROR,
      color: TEXT_LIGHT,
      borderRadius: '0 0 4px 4px',
      borderRadiusGeneral: '4px',
      fieldBorderRadius: '4px 4px 0 0',
    },
    focusColor: BRAND_TERTIARY,
    input: {
      borderColor: BORDER_COLOR,
      borderColorError: ERROR,
    },
    placeholderColor: TEXT_TERTIARY,
    radio: {
      backgroundColor: WHITE,
      borderColor: WHITE,
      borderColorError: ERROR,
      boxShadowColor: BORDER_COLOR,
      selected: BRAND_SECONDARY,
    },
    select: {
      borderColor: BORDER_COLOR,
      borderColorError: ERROR,
    },
    selected: BRAND_SECONDARY,
    submitBarError: {
      backgroundColor: BACKGROUND_ERROR,
      borderRadius: BORDER_RADIUS_SMALL,
      color: ERROR,
    },
    submitBarSuccess: {
      backgroundColor: BACKGROUND_SUCCESS,
      borderRadius: BORDER_RADIUS_SMALL,
      color: SUCCESS,
    },
    title: {
      color: BRAND_SECONDARY,
    },
    text: {
      color: TEXT_DEFAULT,
    },
    password: {
      stages: {
        weak: BRAND_PRIMARY,
        acceptable: BRAND_SECONDARY_DARK,
        strong: BRAND_TERTIARY,
      },
    },
  },

  gallery: {
    backgroundColor: WHITE,
    boxShadow: BOX_SHADOW,
    borderRadius: BORDER_RADIUS_SMALL,
    descriptionColor: TEXT_SECONDARY,
    boxedDescriptionColor: TEXT_SECONDARY,
    image: {
      borderColor: BORDER_COLOR,
      selectedColor: BRAND_TERTIARY,
    },
    videoThumbColor: BRAND_PRIMARY,
  },

  grid: {
    container: {
      sm: '540px',
      md: '720px',
      lg: '960px',
      xl: '1140px',
    },
  },

  iconText: {
    backgroundColor: BACKGROUND_DEFAULT,
    boxShadow: BOX_SHADOW,
    dropShadow: DROP_SHADOW,
    ieBorderColor: BORDER_COLOR,
    borderRadius: BORDER_RADIUS_DEFAULT,
    title: {
      color: HEADING_DEFAULT,
      fontSize: FONT_SIZE_H4,
    },
  },

  languageSelector: {
    backgroundColor: BACKGROUND_DEFAULT,
    checkMarkColor: BRAND_SECONDARY,
    languageCodeColor: HEADING_DEFAULT,
    labelColor: TEXT_TERTIARY,
    borderColor: BORDER_COLOR,
    borderRadius: BORDER_RADIUS_SMALL,
  },

  link: {
    color: BRAND_PRIMARY,
  },

  list: {
    borderRadius: BORDER_RADIUS_SMALL,
    fontSize: FONT_SIZE_DEFAULT,
    title: {
      fontSize: {
        default: '1.25rem',
        md: FONT_SIZE_H4,
        lg: FONT_SIZE_H3,
      },
      minimalFontSize: FONT_SIZE_H5,
    },
    subTitle: {
      fontSize: FONT_SIZE_H5,
      fontWeight: FontWeights.SEMIBOLD,
    },
    listStyles: {
      size: '1.5rem',
      dot: {
        size: '.375rem',
        color: BRAND_SECONDARY,
      },
      check: {
        color: SUCCESS,
      },
      plus: {
        color: SUCCESS,
      },
      cross: {
        color: ERROR,
      },
      minus: {
        color: ERROR,
      },
      numbered: {
        fontSize: '.8rem',
        circleSize: '1.5rem',
        color: BRAND_SECONDARY,
      },
    },
  },

  meta: {
    themeColor: BRAND_PRIMARY,
  },

  modal: {
    backgroundColor: BACKGROUND_DEFAULT,
    borderRadius: BORDER_RADIUS_DEFAULT,
    closeButton: {
      main: BRAND_SECONDARY,
      hover: BRAND_SECONDARY_DARK,
    },
    fontSize: FONT_SIZE_SMALL,
    headerColor: HEADING_DEFAULT,
    sizes: {
      small: '27.5rem',
      medium: '40rem',
      large: '50rem',
    },
    svgIcon: {
      height: '9rem',
      margin: '.25rem auto 1.5rem',
    },
  },

  news: {
    dateColor: TEXT_SECONDARY,
    titleColor: BRAND_PRIMARY,
    linkColor: BRAND_SECONDARY,
    linkHoverColor: BRAND_SECONDARY_DARK,
    overviewTitleColor: BRAND_SECONDARY,
    articleTitleColor: WHITE,
    articleHeaderBackgroundColor: WHITE,
    articleBackgroundColor: BRAND_TERTIARY,
  },

  optionPrice: {
    titleColor: {
      normal: TEXT_DEFAULT,
      active: BRAND_TERTIARY,
    },
    priceColor: {
      normal: TEXT_SECONDARY,
      active: BRAND_TERTIARY,
    },
  },

  optionPriceExtended: {
    toggle: {
      fontSize: FONT_SIZE_SMALL,
      actionColor: BRAND_TERTIARY,
    },
    description: {
      fontSize: FONT_SIZE_SMALL,
    },
    title: {
      fontSize: FONT_SIZE_H5,
    },
  },

  packageCompare: {
    button: {
      borderColor: GRAY,
      borderRadius: BORDER_RADIUS_SMALL,
      textColor: TEXT_SECONDARY,
    },
    title: {
      color: HEADING_DEFAULT,
      fontSize: FONT_SIZE_H5,
    },
    fontSize: FONT_SIZE_SMALL,
    backgroundColor: BACKGROUND_DEFAULT,
    borderRadius: BORDER_RADIUS_SMALL,
    rowBackground: BACKGROUND_LIGHT,
    columnBorder: BORDER_COLOR,
    checkIcon: SUCCESS,
    lineColor: BORDER_COLOR,
    tableText: TEXT_DEFAULT,
    packageTitle: {
      color: TEXT_SECONDARY,
      fontSize: FONT_SIZE_TINY,
    },
    legend: {
      color: TEXT_SECONDARY,
      fontSize: FONT_SIZE_TINY,
    },
  },

  pageheaderHeight: {
    sm: '3.75rem',
    md: '3.75rem',
    lg: '4.25rem',
  },

  pdbConfig: {
    borderColor: BORDER_COLOR,
    headingColor: BRAND_PRIMARY,
    fontSize: FONT_SIZE_H2,
  },

  pdbCTA: {
    checkboxLabelColor: TEXT_SECONDARY,
    deliverytimeColor: SUCCESS,
  },

  pdbInfo: {
    availableText: {
      color: TEXT_SECONDARY,
      fontSize: FONT_SIZE_SMALL,
    },
    borderColor: BORDER_COLOR,
    basicPriceLabel: {
      color: TEXT_DEFAULT,
      fontSize: FONT_SIZE_H5,
    },
    mileageAndDuration: {
      color: TEXT_SECONDARY,
      fontSize: {
        default: FONT_SIZE_TINY,
        lg: FONT_SIZE_SMALL,
      },
    },
    motorSpecs: {
      color: TEXT_SECONDARY,
      fontSize: FONT_SIZE_H5,
    },
    fromPrice: {
      color: BRAND_SECONDARY,
      fontSize: FONT_SIZE_SMALL,
    },
    fromPriceText: {
      color: HEADING_DEFAULT,
      fontSize: FONT_SIZE_SMALL,
    },
    occasionText: {
      color: BRAND_TERTIARY,
      fontSize: FONT_SIZE_SMALL,
    },
    price: {
      color: BRAND_PRIMARY,
      fontSize: FONT_SIZE_H1,
    },
    title: {
      color: HEADING_DEFAULT,
      fontSize: {
        default: FONT_SIZE_H4,
        md: FONT_SIZE_H3,
        lg: FONT_SIZE_H2,
      },
    },
  },

  progressRow: {
    backgroundColor: BACKGROUND_DEFAULT,
    borderColor: BORDER_COLOR,
    hamburgerColor: BRAND_PRIMARY,
    price: {
      color: BRAND_SECONDARY,
      fontSize: FONT_SIZE_H5,
    },
    steps: MAIN_MENU_SCHEME,
    stepsFontSize: FONT_SIZE_H6,
  },

  review: {
    backgroundColor: BACKGROUND_DEFAULT,
    starColor: BRAND_SECONDARY,
    rating: {
      fontSize: FONT_SIZE_TINY,
    },
    link: {
      fontSize: FONT_SIZE_SMALL,
    },
    textColor: BRAND_TERTIARY,
    default: {
      borderColor: BORDER_COLOR,
      ratingColor: BRAND_PRIMARY,
      reviewCountColor: TEXT_SECONDARY,
      textRatingColor: TEXT_SECONDARY,
      reviewLink: SECONDARY_BTN_SCHEME,
    },
    hero: {
      borderColor: WHITE,
      ratingColor: WHITE,
      reviewCountColor: WHITE,
      textRatingColor: WHITE,
      reviewLink: {
        main: WHITE,
        hover: WHITE,
        active: WHITE,
        contrast: WHITE,
        visited: WHITE,
      },
    },
  },

  row: {
    gradientColor: BLACK,
  },

  schemes: {
    default: DEFAULT_SCHEME,
    defaultLight: DEFAULT_LIGHT_SCHEME,
    white: WHITE_SCHEME,
    light: LIGHT_SCHEME,
    dark: DARK_SCHEME,
    primary: PRIMARY_SCHEME,
    secondary: SECONDARY_SCHEME,
    tertiary: TERTIARY_SCHEME,
  },

  slider: {
    button: {
      backgroundColor: BACKGROUND_LIGHT,
      chevronColor: BRAND_PRIMARY,
    },
    dot: {
      selected: SILVER,
      unselected: BRAND_PRIMARY,
    },
  },

  specifications: {
    borderRadius: BORDER_RADIUS_SMALL,
    keyColor: TEXT_SECONDARY,
    valueColor: TEXT_DEFAULT,
    rowBorderColor: BORDER_COLOR,
    backgroundColor: BACKGROUND_LIGHT,
    titleColor: HEADING_DEFAULT,
  },

  tabs: {
    activeTabBackground: BRAND_PRIMARY,
    activeTabBorderColor: BRAND_PRIMARY,
    activeTabTextColor: WHITE,
    borderRadius: BORDER_RADIUS_SMALL,
    tabBackground: WHITE,
    tabBorderColor: BORDER_COLOR,
    tabTextColor: BLACK,
  },

  tags: {
    backgroundColor: BACKGROUND_LIGHT,
    color: SUCCESS,
    fontSize: '.7rem',
    ratioLarge: 1.2,
  },

  tileSelect: {
    borderColor: BRAND_TERTIARY,
    borderRadius: BORDER_RADIUS_SMALL,
    backgroundColor: BACKGROUND_DEFAULT,
    mostChosen: {
      color: TEXT_LIGHT,
      backgroundColor: BRAND_TERTIARY,
      borderRadius: BORDER_RADIUS_SMALL,
    },
  },

  tooltip: {
    backgroundColor: BACKGROUND_DEFAULT,
    borderColor: BORDER_COLOR,
    borderRadius: BORDER_RADIUS_SMALL,
    titleColor: HEADING_DEFAULT,
    contentColor: TEXT_SECONDARY,
    bulletColor: BRAND_PRIMARY,
    iconColor: BRAND_PRIMARY,
  },

  wysiwyg: {
    fontSize: FONT_SIZE_DEFAULT,
    primary: BRAND_PRIMARY,
    dark: TEXT_DEFAULT,
    white: TEXT_LIGHT,
    templates: {
      default: {
        h1: {
          fontSize: {
            default: FONT_SIZE_H1,
          },
          lineHeight: '1.375em',
        },
        h2: {
          fontSize: {
            default: FONT_SIZE_H2,
          },
          lineHeight: '1.25',
        },
        h3: {
          fontSize: {
            default: FONT_SIZE_H3,
          },
          lineHeight: '1.5',
        },
        h4: {
          fontSize: {
            default: FONT_SIZE_H4,
          },
          lineHeight: '1.25',
        },
        h5: {
          fontSize: {
            default: FONT_SIZE_H5,
          },
          lineHeight: '1',
        },
        h6: {
          fontSize: {
            default: FONT_SIZE_H6,
          },
          lineHeight: '1',
        },
        p: {
          fontSize: {
            default: FONT_SIZE_DEFAULT,
          },
          lineHeight: '1.6',
        },
        subheadingfontWeight: FontWeights.SEMIBOLD,
      },
      hero: {
        h1: {
          paddingBottom: '1rem',
          lineHeight: '2.5rem',
          fontWeight: FontWeights.SEMIBOLD,
          fontFamily: FONT_ACCENT,
        },
        h2: {
          fontFamily: FONT_ACCENT,
        },
        h3: {
          fontWeight: FontWeights.REGULAR,
        },
        h4: {
          fontWeight: FontWeights.REGULAR,
        },
        h5: {
          fontWeight: FontWeights.REGULAR,
          lineHeight: '1.5',
        },
        subheadingfontWeight: FontWeights.REGULAR,
      },
      banner: {
        h1: {},
        h2: {},
      },
    },
  },
  orderForm: {
    subtitleColor: BRAND_SECONDARY,
    borderColor: SILVER,
  },
  menu: {
    desktop: {
      linkColor: BRAND_PRIMARY,
      linkHoverColor: BRAND_SECONDARY,
      backgroundHoverColor: WHITE,
      borderRadius: BORDER_RADIUS_DEFAULT,
      overlayColor: 'rgba(23, 23, 23, 0.5)',
      overlayLinkColor: '#fafafa',
      levelOne: {
        backgroundColor: WHITE,
        backgroundHoverColor: BACKGROUND_LIGHT,
      },
      levelTwo: {
        backgroundColor: BACKGROUND_LIGHT,
      },
    },
    touch: {
      linkColor: BRAND_PRIMARY,
      linkHoverColor: BLACK,
      header: {
        backgroundColor: BACKGROUND_LIGHT,
        titleColor: BRAND_SECONDARY,
      },
      contact: {
        backgroundColor: WHITE,
        color: GRAY_DARK,
      },
      toggleButton: {
        backgroundColor: WHITE,
        backgroundHoverColor: BACKGROUND_LIGHT,
        borderColor: SILVER,
        borderRadius: BORDER_RADIUS_DEFAULT,
      },
      item: {
        backgroundColor: WHITE,
        backgroundHoverColor: BACKGROUND_LIGHT,
        border: '2px solid #e6e8ec',
      },
      subItem: {
        backgroundColor: WHITE,
      },
      backLink: {
        labelColor: BRAND_PRIMARY,
        arrowColor: BRAND_PRIMARY,
        hoverColor: BLACK,
      },
      closeButton: {
        color: BRAND_PRIMARY,
        hoverColor: BLACK,
      },
    },
    footer: {
      linkColor: BRAND_PRIMARY,
      linkActiveColor: BRAND_SECONDARY,
    },
  },
}: Theme);

export default (
  defaultParams: [DefaultThemeParams],
  theme: Object = {},
): Theme => merge(defaultTheme.apply(this, defaultParams), theme);
